import { useState } from "react";
import { Plus, Edit2, Archive, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useBannerManagement } from "@/admin/hooks/useBannerManagement";
import { BannerForm } from "@/admin/components/BannerForm";

export default function AdminBanners() {
  const { query, archiveMutation } = useBannerManagement();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const banners = query.data ?? [];
  const active = banners.filter((b) => !b.archived_at && b.is_active);
  const inactive = banners.filter((b) => !b.archived_at && !b.is_active);
  const archived = banners.filter((b) => b.archived_at);

  const openNew = () => { setEditingId(null); setDialogOpen(true); };
  const openEdit = (id: string) => { setEditingId(id); setDialogOpen(true); };

  const handleArchive = (id: string) => {
    if (window.confirm("Archiver ce bandeau ?")) {
      archiveMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bandeaux d'annonce</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gérez les bandeaux affichés en haut du site (Ramadan, Aïd, etc.)
          </p>
        </div>
        <Button onClick={openNew} className="gap-2">
          <Plus className="h-4 w-4" />
          Nouveau bandeau
        </Button>
      </div>

      {/* Actifs */}
      {active.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4 text-green-600" />
              Actif ({active.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {active.map((b) => (
              <BannerRow
                key={b.id}
                banner={b}
                onEdit={() => openEdit(b.id)}
                onArchive={() => handleArchive(b.id)}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Inactifs */}
      {inactive.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-muted-foreground">
              <EyeOff className="h-4 w-4" />
              Inactifs ({inactive.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {inactive.map((b) => (
              <BannerRow
                key={b.id}
                banner={b}
                onEdit={() => openEdit(b.id)}
                onArchive={() => handleArchive(b.id)}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Vide */}
      {banners.filter((b) => !b.archived_at).length === 0 && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <p className="mb-4">Aucun bandeau. Créez le premier !</p>
            <Button onClick={openNew} variant="outline" className="gap-2">
              <Plus className="h-4 w-4" />
              Créer un bandeau Ramadan
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Archivés */}
      {archived.length > 0 && (
        <Card className="opacity-60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              Archivés ({archived.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {archived.map((b) => (
              <div key={b.id} className="flex items-center gap-3 text-sm text-muted-foreground">
                <div className="h-4 w-4 rounded" style={{ backgroundColor: b.background_color }} />
                <span className="flex-1">{b.title_fr}</span>
                <span className="text-xs">Archivé le {new Date(b.archived_at!).toLocaleDateString("fr-FR")}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Dialog formulaire */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifier le bandeau" : "Nouveau bandeau"}</DialogTitle>
          </DialogHeader>
          <BannerForm
            bannerId={editingId}
            onSuccess={() => setDialogOpen(false)}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BannerRow({
  banner,
  onEdit,
  onArchive,
}: {
  banner: { id: string; title_fr: string; title_ar: string; background_color: string; text_color: string; is_active: boolean };
  onEdit: () => void;
  onArchive: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border p-3">
      <div
        className="h-8 w-8 rounded flex-shrink-0 flex items-center justify-center text-xs font-bold"
        style={{ backgroundColor: banner.background_color, color: banner.text_color }}
      >
        A
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{banner.title_fr}</p>
        <p className="text-xs text-muted-foreground truncate" dir="rtl">{banner.title_ar}</p>
      </div>
      <Badge variant={banner.is_active ? "default" : "secondary"} className="flex-shrink-0">
        {banner.is_active ? "Actif" : "Inactif"}
      </Badge>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" onClick={onEdit} className="h-8 w-8">
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" onClick={onArchive} className="h-8 w-8 text-muted-foreground hover:text-destructive">
          <Archive className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
